/**
 * EmployeeDetailDrawer
 *
 * Rechts-seitiger Drawer: Vollständiges Risiko-Breakdown eines Mitarbeiters
 * + KI-generierte FK-Maßnahmenempfehlungen basierend auf mindsquare Core Values
 * und Leading Simple.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CalendarRange,
  CheckCheck,
  CheckCircle2,
  ChevronRight,
  Circle,
  ClipboardList,
  Clock,
  Database,
  Info,
  Plus,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  UserX,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useInProbezeit } from "../hooks/useProbezeitPerson.js";
import {
  type EmployeeAnalysis,
  type Intervention,
  type InterventionStatus,
  type InterventionType,
  type ManagerTip,
  type WirtschaftAlert,
  type WirtschaftAmpelLevel,
  type WirtschaftKohorte,
  aiApi,
  employeesApi,
  interventionsApi,
  wirtschaftApi,
} from "../lib/api.js";
import { GLOSSAR } from "../lib/glossar.js";
import { arbeitstageText } from "../lib/krankheitstage.js";
import {
  massnahmenFehlerText,
  massnahmenHinweisText,
  massnahmenListenZustand,
} from "../lib/massnahmen-anzeige.js";
import { EigeneKrankheitstage } from "./EigeneKrankheitstage.js";
import { ProbezeitWirtschaftBlock } from "./probezeit/ProbezeitWirtschaftBlock.js";
import { RiskBadge } from "./ui/RiskBadge.js";

// ── Helpers ───────────────────────────────────────────────────────────────────

function riskColor(level: string) {
  if (level === "high") return "var(--risk-high)";
  if (level === "medium") return "var(--risk-medium)";
  return "var(--risk-low)";
}

/** Farbiger TEXT nutzt die kontrastgeprüften -text-Varianten (≥ 4.5:1). */
function riskTextColor(level: string) {
  if (level === "high") return "var(--risk-high-text)";
  if (level === "medium") return "var(--risk-medium-text)";
  return "var(--risk-low-text)";
}

const TIMEFRAME_META: Record<
  ManagerTip["timeframe"],
  { label: string; Icon: typeof Clock; color: string; chipBg: string }
> = {
  sofort: {
    label: "Diese Woche",
    Icon: Clock,
    color: "var(--risk-high-text)",
    chipBg: "var(--risk-high-bg)",
  },
  monat: {
    label: "Diesen Monat",
    Icon: Calendar,
    color: "var(--accent)",
    chipBg: "var(--accent-dim)",
  },
  quartal: {
    label: "3–6 Monate",
    Icon: CalendarRange,
    color: "var(--accent-gold)",
    chipBg: "var(--accent-gold-dim)",
  },
};

// ── W26: Wirtschaftlichkeit — Reiter, Formatierung, Ampel ─────────────────────

type DrawerTab = "ueberblick" | "bindung" | "wirtschaft";

const DRAWER_TABS: Array<{ key: DrawerTab; label: string }> = [
  { key: "ueberblick", label: "Überblick" },
  { key: "bindung", label: "Bindung" },
  { key: "wirtschaft", label: "Wirtschaftlichkeit" },
];

const fmtEur0 = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

/** Kompakte €-Angabe für enge Kacheln — 0 bleibt "0 €" (nur null wird "—"). */
const fmtEurKompakt = (n: number): string => {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2).replace(".", ",")}M €`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1).replace(".", ",")}k €`;
  return fmtEur0.format(n);
};

const fmtEurAxis = (v: number): string =>
  Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : String(v);

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mär",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
];

/** "2026-07" → "Jul 26" */
function fmtYearMonth(ym: string): string {
  const idx = Number(ym.slice(5, 7)) - 1;
  const name = MONTH_SHORT[idx];
  return name ? `${name} ${ym.slice(2, 4)}` : ym;
}

const AMPEL_META: Record<
  WirtschaftAmpelLevel,
  { label: string; color: string; dot: string; bg: string }
> = {
  ok: {
    label: "OK",
    color: "var(--risk-low-text)",
    dot: "var(--risk-low)",
    bg: "var(--risk-low-bg)",
  },
  warning: {
    label: "Warnung",
    color: "var(--risk-medium-text)",
    dot: "var(--risk-medium)",
    bg: "var(--risk-med-bg)",
  },
  critical: {
    label: "Kritisch",
    color: "var(--risk-high-text)",
    dot: "var(--risk-high)",
    bg: "var(--risk-high-bg)",
  },
};

/**
 * Einordnung in die Vergleichsgruppe — die Antwort auf „ist dieser Fall normal
 * oder aussergewoehnlich?".
 *
 * Anlass (Marie, 13.08.2026, Hebel 4): Der Fuehrungskraft fehlt bei einer
 * Gehaltsentscheidung nicht die Erlaubnis, sondern der Vergleich. Ohne ihn
 * fuehlt sich jede Ausnahme wie eine *„super, super Sonderentscheidung"* an —
 * *„den Mut nicht haben, diese Entscheidung zu treffen, und dann verlieren wir
 * die Leute."*
 *
 * Bewusst OHNE Ampel: Ein niedriger Faktor ist kein Fehler, sondern ein
 * Gespraechsanlass. Das Tool ordnet ein, es bewertet nicht.
 *
 * ⚠️ Seit dem 04.09.2026 NICHT gerendert — Maries Zuruf: „nimm diese Ansicht
 * erst wieder raus“. Die Komponente bleibt stehen, weil „erst wieder“ nach einer
 * Rückkehr klingt; die Einbindung steht auskommentiert an ihrer Stelle im
 * Wirtschaftlichkeits-Tab. `export` nur, damit sie nicht als ungenutzt gilt.
 */
export function KohortenEinordnung({ kohorte }: { kohorte: WirtschaftKohorte }) {
  const fak = (n: number) => n.toFixed(2).replace(".", ",");
  const hatGruppe = kohorte.median !== null && kohorte.p25 !== null && kohorte.p75 !== null;

  // Skala so waehlen, dass Gruppe UND eigener Wert hineinpassen.
  const obenRoh = Math.max(kohorte.p75 ?? kohorte.faktor, kohorte.faktor);
  const oben = obenRoh * 1.15 || 1;
  const pos = (v: number) => Math.max(0, Math.min(100, (v / oben) * 100));

  return (
    <div>
      <p
        className="text-xs font-semibold uppercase tracking-wider mb-2"
        style={{ color: "var(--text-muted)" }}
      >
        Im Vergleich · gleiche Karrierestufe
      </p>
      <div
        className="rounded p-3"
        style={{ background: "var(--surface-2, rgba(127,127,127,0.06))" }}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>
            {fak(kohorte.faktor)}×
          </span>
          <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Jahresumsatz je Euro Gehalt
          </span>
        </div>

        {hatGruppe ? (
          <>
            {/* Balken = mittlere Haelfte der Gruppe, Strich = Median, Punkt = diese Person */}
            <div
              className="relative mt-3 mb-1"
              style={{ height: 10, borderRadius: 999, background: "var(--border, #d9dde3)" }}
            >
              <div
                className="absolute"
                style={{
                  left: `${pos(kohorte.p25 as number)}%`,
                  width: `${pos(kohorte.p75 as number) - pos(kohorte.p25 as number)}%`,
                  top: 0,
                  bottom: 0,
                  borderRadius: 999,
                  background: "var(--accent-soft, rgba(90,130,180,0.45))",
                }}
              />
              <div
                className="absolute"
                style={{
                  left: `${pos(kohorte.median as number)}%`,
                  top: -2,
                  bottom: -2,
                  width: 2,
                  background: "var(--text-muted)",
                }}
              />
              <div
                className="absolute"
                style={{
                  left: `${pos(kohorte.faktor)}%`,
                  top: "50%",
                  width: 14,
                  height: 14,
                  marginLeft: -7,
                  marginTop: -7,
                  borderRadius: 999,
                  background: "var(--surface, #fff)",
                  border: "3px solid var(--accent)",
                }}
              />
            </div>
            <p className="text-xs mt-2" style={{ color: "var(--text-secondary)" }}>
              {kohorte.gruppe} · {kohorte.gruppengroesse} Personen · Gruppe{" "}
              {fak(kohorte.p25 as number)} – <b>{fak(kohorte.median as number)}</b> –{" "}
              {fak(kohorte.p75 as number)}
            </p>
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              Liegt höher als <b>{kohorte.perzentil} %</b> der Vergleichsgruppe.
            </p>
          </>
        ) : (
          <p className="text-xs mt-2" style={{ color: "var(--text-secondary)" }}>
            {kohorte.gruppe} · nur {kohorte.gruppengroesse}{" "}
            {kohorte.gruppengroesse === 1 ? "Person" : "Personen"} mit vergleichbaren Daten — zu
            wenig für einen Vergleich.
          </p>
        )}

        {kohorte.festpreis12m > 0 && (
          <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
            Dazu {new Intl.NumberFormat("de-DE").format(Math.round(kohorte.festpreis12m))} €
            Festpreis-Umsatz aus geleiteten Projekten. Im Faktor nicht enthalten — er gehört zum
            Projekt, nicht zur eigenen Faktura.
          </p>
        )}
      </div>
    </div>
  );
}

/** Wirtschafts-Ampel als Badge mit Punkt-Farbe — Pendant zur RiskBadge (Bindung). */
function WirtschaftAmpelBadge({ level }: { level: WirtschaftAmpelLevel }) {
  const meta = AMPEL_META[level];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
      style={{ color: meta.color, background: meta.bg }}
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: meta.dot }} />
      {meta.label}
    </span>
  );
}

/** KPI-Kachel mit Formel-Popover aus dem Glossar (Muster: ui/KpiCard). */
function WirtKpiTile({
  label,
  value,
  valueColor,
  glossarEntry,
  hero = false,
}: {
  label: string;
  value: string;
  valueColor?: string;
  glossarEntry?: { formel: string; hinweis: string };
  /** Hero-Kachel: doppelte Breite, größere Zahl (z. B. „Umsatz 12M"). */
  hero?: boolean;
}) {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div className={hero ? "relative card p-3 col-span-2" : "relative card p-3"}>
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <span
          // Kein `truncate`: Lange Labels brechen um, statt abgeschnitten zu
          // werden. "Deckungsbeitrag / Marge Ø3M" brauchte 173 px bei 171 px
          // Platz — zwei Pixel, und die Hälfte des Namens war weg (04.09.2026).
          className="text-[9px] font-bold tracking-widest uppercase leading-tight"
          style={{ color: "var(--text-muted)" }}
        >
          {label}
        </span>
        {glossarEntry && (
          <button
            type="button"
            className="p-0.5 rounded-full shrink-0 transition-colors"
            style={{ color: "var(--text-muted)" }}
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => setShowInfo(false)}
            onClick={() => setShowInfo((s) => !s)}
            aria-label={`Formel zu ${label}`}
          >
            <Info size={11} />
          </button>
        )}
      </div>
      <div
        className={`${hero ? "text-2xl" : "text-base"} font-bold font-mono-data tabular-nums`}
        style={{ color: valueColor ?? "var(--text-primary)" }}
      >
        {value}
      </div>
      {showInfo && glossarEntry && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-30 rounded-lg p-3 text-[11px] leading-relaxed"
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            color: "var(--text-secondary)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <p
            className="font-mono-data text-[10px] leading-relaxed mb-1.5"
            style={{ color: "var(--text-primary)" }}
          >
            {glossarEntry.formel}
          </p>
          <p>{glossarEntry.hinweis}</p>
        </div>
      )}
    </div>
  );
}

const DEMO_HINWEIS =
  "Demo-Datenbasis (Etappe 0) — echte Salesforce-Zahlen folgen mit dem Datenfundament";

/**
 * Banner zur Demo-Datenbasis. Bewusst reiterunabhängig verfügbar: der Überblick
 * zeigt ebenfalls Demo-Werte (Wirtschafts-Ampel, Auslastung Ø3M, Umsatz 12M),
 * und aus Retention Radar / Cockpit heraus liegt kein Seiten-Banner dahinter —
 * ohne Kennzeichnung sähen die Zahlen dort wie echte Personio-Daten aus.
 */
function DemoDatenbasisBanner() {
  return (
    <div
      className="flex items-start gap-2 rounded-lg px-3 py-2.5"
      style={{
        background: "var(--accent-gold-dim)",
        border: "1px solid var(--accent-gold-dim)",
      }}
    >
      <Database size={12} style={{ color: "var(--accent-gold)", flexShrink: 0, marginTop: 2 }} />
      <span className="text-[11px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
        {DEMO_HINWEIS}
      </span>
    </div>
  );
}

/** Dezenter Marker in der Reiter-Leiste — sichtbar in JEDEM Reiter, auch „Bindung". */
function DemoTabMarker() {
  return (
    <span
      title={DEMO_HINWEIS}
      className="ml-auto self-center flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
      style={{
        color: "var(--accent-gold)",
        background: "var(--accent-gold-dim)",
        border: "1px solid var(--accent-gold-dim)",
      }}
    >
      <Database size={9} />
      Demo
    </span>
  );
}

/** Eine Zeile der Ampel-Alert-Liste (Warnung / Kritisch). */
function WirtschaftAlertRow({ alert }: { alert: WirtschaftAlert }) {
  const critical = alert.severity === "critical";
  // Icon + farbiger Text der Severity-Zeile nutzen die -text-Varianten (≥ 4.5:1)
  const color = critical ? "var(--risk-high-text)" : "var(--risk-medium-text)";
  const Icon = critical ? AlertTriangle : AlertCircle;
  return (
    <div
      className="flex items-start gap-2 rounded-lg p-3"
      style={{
        background: critical ? "var(--risk-high-bg)" : "var(--risk-med-bg)",
        border: "1px solid var(--border)",
      }}
    >
      <Icon size={13} style={{ color, flexShrink: 0, marginTop: 1 }} />
      <div className="flex-1 min-w-0">
        <span
          className="text-[10px] font-semibold uppercase tracking-wider block mb-0.5"
          style={{ color }}
        >
          {critical ? "Kritisch" : "Warnung"}
        </span>
        <span className="text-[11px] leading-relaxed" style={{ color: "var(--text-primary)" }}>
          {alert.message}
        </span>
      </div>
    </div>
  );
}

// ── Signal Breakdown ──────────────────────────────────────────────────────────

function SignalRow({
  signal,
  maxWeight,
}: { signal: EmployeeAnalysis["signals"][0]; maxWeight: number }) {
  const pct = Math.round((signal.weight / maxWeight) * 100);
  const color = signal.triggered
    ? signal.weight >= 0.2
      ? "var(--risk-high)"
      : signal.weight >= 0.1
        ? "var(--accent)"
        : "var(--accent-gold)"
    : "var(--bg-hover)";

  return (
    <div
      className="flex items-start gap-2 py-1.5"
      style={{ borderBottom: "1px solid var(--border)" }}
    >
      <div className="mt-0.5 shrink-0">
        {signal.triggered ? (
          <ShieldAlert size={12} style={{ color }} />
        ) : (
          <Circle size={12} style={{ color: "var(--border-light)", opacity: 0.5 }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <span
          className="text-[11px] leading-tight block"
          style={{
            color: signal.triggered ? "var(--text-primary)" : "var(--text-muted)",
            opacity: signal.triggered ? 1 : 0.6,
          }}
        >
          {signal.label}
        </span>
        {signal.triggered && (
          <div
            className="mt-1 h-1 rounded-full overflow-hidden"
            style={{ background: "var(--bg-surface)" }}
          >
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, background: color }}
            />
          </div>
        )}
      </div>
      <span
        className="text-[10px] font-mono shrink-0 mt-0.5"
        style={{
          color: signal.triggered ? color : "var(--border-light)",
          opacity: signal.triggered ? 1 : 0.5,
        }}
      >
        {(signal.weight * 100).toFixed(0)}%
      </span>
    </div>
  );
}

// ── Tip Card ──────────────────────────────────────────────────────────────────

function TipCard({ tip }: { tip: ManagerTip }) {
  const meta = TIMEFRAME_META[tip.timeframe];
  const Icon = meta.Icon;

  return (
    <div
      className="rounded-lg p-3 flex flex-col gap-2"
      style={{ background: "var(--bg-surface)", border: "1px solid var(--border)" }}
    >
      <div className="flex items-center gap-2">
        <Icon size={11} style={{ color: meta.color, flexShrink: 0 }} />
        <span
          className="text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: meta.color }}
        >
          {meta.label}
        </span>
        <span
          className="ml-auto text-[10px] px-1.5 py-0.5 rounded"
          style={{ background: meta.chipBg, color: meta.color }}
        >
          {tip.coreValue}
        </span>
      </div>
      <p className="text-xs leading-relaxed" style={{ color: "var(--text-primary)" }}>
        {tip.action}
      </p>
      <p
        className="text-[11px] leading-relaxed"
        style={{
          color: "var(--text-muted)",
          borderLeft: "2px solid var(--border)",
          paddingLeft: 8,
        }}
      >
        {tip.why}
      </p>
    </div>
  );
}

// ── Main Drawer ───────────────────────────────────────────────────────────────

interface Props {
  employeeId: number | null;
  onClose: () => void;
}

const INTERVENTION_LABELS: Record<InterventionType, string> = {
  einzel_gespraech: "Einzelgespräch",
  gehaltsreview: "Gehalts-Review",
  entwicklungsgespraech: "Entwicklungsgespräch",
  versetzungsangebot: "Versetzungsangebot",
  sonstiges: "Sonstiges",
};

const STATUS_META: Record<InterventionStatus, { label: string; color: string }> = {
  open: { label: "Offen", color: "var(--text-muted)" },
  in_progress: { label: "In Bearbeitung", color: "var(--accent)" },
  done: { label: "Erledigt", color: "var(--risk-low)" },
};

function InterventionItem({
  intervention,
  onStatusChange,
}: {
  intervention: Intervention;
  onStatusChange: (id: string, status: InterventionStatus) => void;
}) {
  const meta = STATUS_META[intervention.status];
  return (
    <div
      className="rounded p-3 flex flex-col gap-1.5"
      style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium flex-1" style={{ color: "var(--text-primary)" }}>
          {INTERVENTION_LABELS[intervention.type]}
        </span>
        <select
          value={intervention.status}
          onChange={(e) => onStatusChange(intervention.id, e.target.value as InterventionStatus)}
          className="text-[10px] px-1.5 py-0.5 rounded"
          style={{
            background: "var(--bg-hover)",
            border: "1px solid var(--border)",
            color: meta.color,
            outline: "none",
          }}
        >
          <option value="open">Offen</option>
          <option value="in_progress">In Bearbeitung</option>
          <option value="done">Erledigt</option>
        </select>
      </div>
      {intervention.notes && (
        <p className="text-[11px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          {intervention.notes}
        </p>
      )}
      <span className="text-[9px]" style={{ color: "var(--text-muted)" }}>
        {new Date(intervention.updatedAt).toLocaleDateString("de-DE")}
      </span>
    </div>
  );
}

export function EmployeeDetailDrawer({ employeeId, onClose }: Props) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newType, setNewType] = useState<InterventionType>("einzel_gespraech");
  const [newNotes, setNewNotes] = useState("");
  // W26 Ebene 2: Reiter-Zustand — Überblick ist der Standard
  const [tab, setTab] = useState<DrawerTab>("ueberblick");

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // W26: Der Personenwechsel wird strukturell an den Aufrufstellen gelöst —
  // dort trägt der Drawer ein key={selectedEmployeeId} und wird komplett neu
  // gemountet. Damit fallen Reiter UND Formular-States (showNewForm/newType/
  // newNotes) gemeinsam auf den Startwert zurück. Kein feldweises Zurücksetzen
  // hier ergänzen: das wird beim nächsten neuen State wieder vergessen.

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) onClose();
  };

  const {
    data: analysis,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["employee-analysis", employeeId],
    queryFn: () => employeesApi.getAnalysis(employeeId!),
    enabled: employeeId !== null,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const {
    data: tips,
    isLoading: tipsLoading,
    isError: tipsError,
    refetch: fetchTips,
    isFetched: tipsFetched,
  } = useQuery({
    queryKey: ["employee-tips", employeeId],
    queryFn: () => aiApi.getEmployeeTips(employeeId!),
    enabled: false, // manuell auslösen
    staleTime: 30 * 60_000,
    retry: 0,
  });

  // Interventions
  const {
    data: interventionData,
    refetch: refetchInterventions,
    status: interventionStatus,
    error: interventionError,
  } = useQuery({
    queryKey: ["interventions", employeeId],
    queryFn: () => interventionsApi.getForEmployee(employeeId!),
    enabled: employeeId !== null,
    staleTime: 2 * 60_000,
  });

  // W26: Wirtschaftsdaten — lazy: nur laden, wenn Überblick oder
  // Wirtschaftlichkeit sichtbar ist (der Bindung-Reiter löst keinen Request aus)
  const {
    data: wirtschaft,
    isLoading: wirtschaftLoading,
    isError: wirtschaftError,
  } = useQuery({
    queryKey: ["wirtschaft-employee", employeeId],
    queryFn: () => wirtschaftApi.getEmployee(employeeId!),
    enabled: employeeId !== null && (tab === "ueberblick" || tab === "wirtschaft"),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  // „seit Eintritt" gilt nur in der Probezeit. Der Server entscheidet; die Antwort teilt sich
  // der Hook mit dem Block im Reiter „Überblick", es geht also eine Anfrage raus statt zwei.
  // Dass sie bei jedem Reiterwechsel nicht erneut rausgeht, liegt nicht an dieser Reihenfolge,
  // sondern daran, dass „nicht in der Probezeit" eine Antwort ist und kein Fehler — siehe
  // `useProbezeitPerson`. Erst nach dem Profil zu fragen, hält beide im selben Schritt.
  const inProbezeit = useInProbezeit(analysis?.id ?? null);

  const createMutation = useMutation({
    mutationFn: (data: { type: InterventionType; notes: string }) =>
      interventionsApi.create({ employeeId: employeeId!, ...data }),
    onSuccess: () => {
      void refetchInterventions();
      setShowNewForm(false);
      setNewNotes("");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: InterventionStatus }) =>
      interventionsApi.update(id, { status }),
    onSuccess: () => void refetchInterventions(),
  });

  if (employeeId === null) return null;

  const maxWeight = analysis ? Math.max(...analysis.signals.map((s) => s.weight), 0.01) : 0.01;

  const triggered = analysis?.signals.filter((s) => s.triggered && !s.positive) ?? [];
  const positives = analysis?.signals.filter((s) => s.triggered && s.positive) ?? [];
  const untriggered = analysis?.signals.filter((s) => !s.triggered) ?? [];

  // Maßnahmen: Was der Block zeigt, entscheiden die Regeln in `lib/massnahmen-anzeige.ts`. Nach
  // einem Fehler behält React Query die Daten des letzten Erfolgs. Sie gelten hier nicht mehr,
  // sonst stünden neben dem Hinweis eine veraltete Liste und veraltete Zahlen.
  const massnahmenDaten = interventionStatus === "success" ? interventionData : undefined;
  const massnahmenZustand = massnahmenListenZustand({
    status: interventionStatus,
    anzahl: interventionData?.interventions.length ?? 0,
  });

  const timeframeOrder: ManagerTip["timeframe"][] = ["sofort", "monat", "quartal"];
  const tipsByTimeframe = tips
    ? timeframeOrder
        .map((tf) => ({ tf, items: tips.tips.filter((t) => t.timeframe === tf) }))
        .filter((g) => g.items.length > 0)
    : [];

  // ── W26 Reiter „Überblick": beide Ampeln, drei Schlagzahlen, Top-Signale ──

  const renderUeberblick = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-32 gap-2">
          <RefreshCw size={16} className="animate-spin" style={{ color: "var(--accent)" }} />
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            Profil wird geladen…
          </span>
        </div>
      );
    }
    if (isError || !analysis) {
      return (
        <div className="flex items-center gap-2 py-4">
          <AlertCircle size={14} style={{ color: "var(--risk-high)" }} />
          <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Fehler beim Laden des Profils
          </span>
        </div>
      );
    }

    const topBindung = triggered
      .slice()
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 3);
    const topAlerts = wirtschaft?.ampel.alerts.slice(0, 3) ?? [];

    return (
      <>
        {/* Demo-Banner (Etappe 0) — Wirtschafts-Ampel und zwei der drei
            Schlagzahlen unten sind Demo-Werte, nicht Personio-Realdaten */}
        {wirtschaft?.dataBasis === "demo" && <DemoDatenbasisBanner />}

        {/* Beide Ampeln */}
        <div className="grid grid-cols-2 gap-2">
          <div className="card p-3">
            <p
              className="text-[9px] font-bold tracking-widest uppercase mb-2"
              style={{ color: "var(--text-muted)" }}
            >
              Bindung
            </p>
            <RiskBadge level={analysis.riskLevel} />
          </div>
          <div className="card p-3">
            <p
              className="text-[9px] font-bold tracking-widest uppercase mb-2"
              style={{ color: "var(--text-muted)" }}
            >
              Wirtschaft
            </p>
            {wirtschaft ? (
              <WirtschaftAmpelBadge level={wirtschaft.ampel.level} />
            ) : (
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                {wirtschaftLoading ? "lädt…" : "—"}
              </span>
            )}
          </div>
        </div>

        {/* Drei Schlagzahlen — größer als die Ampel-Karten, klare Hierarchie */}
        <div className="grid grid-cols-3 gap-2">
          <div className="card p-3 text-center">
            <div
              className="text-2xl font-bold font-mono-data tabular-nums"
              style={{ color: riskTextColor(analysis.riskLevel) }}
            >
              {(analysis.retentionScore * 100).toFixed(0)}%
            </div>
            <div
              className="text-[9px] uppercase tracking-wider mt-0.5"
              style={{ color: "var(--text-muted)" }}
            >
              Score
            </div>
          </div>
          <div className="card p-3 text-center">
            <div
              className="text-2xl font-bold font-mono-data tabular-nums"
              style={{ color: "var(--text-primary)" }}
            >
              {wirtschaft?.kpis.utilizationAvg3m != null
                ? `${wirtschaft.kpis.utilizationAvg3m} %`
                : "—"}
            </div>
            <div
              className="text-[9px] uppercase tracking-wider mt-0.5"
              style={{ color: "var(--text-muted)" }}
            >
              {/* In Einheiten ohne Faktura-Ziel (MTB) wird die Zahl gezeigt, aber
                  nicht bewertet — sonst läse sich eine niedrige Auslastung ohne
                  Warnung wie ein grünes Ergebnis. */}
              {wirtschaft?.kpis.utilizationRated === false
                ? "Auslastung Ø3M · ohne Ziel"
                : "Auslastung Ø3M"}
            </div>
          </div>
          <div className="card p-3 text-center">
            <div
              className="text-2xl font-bold font-mono-data tabular-nums"
              style={{ color: "var(--text-primary)" }}
            >
              {wirtschaft ? fmtEurKompakt(wirtschaft.kpis.umsatz12m) : "—"}
            </div>
            <div
              className="text-[9px] uppercase tracking-wider mt-0.5"
              style={{ color: "var(--text-muted)" }}
            >
              Umsatz 12M
            </div>
          </div>
        </div>

        {/* ── Wirtschaftlichkeit in der Probezeit (Spezifikation R7) ── */}
        {/* Ohne Hülle: Der Block zeigt für Nicht-Probezeit-Personen nichts, eine leere Hülle mit
            Außenabstand ließe trotzdem eine Lücke im Reiter. */}
        <ProbezeitWirtschaftBlock personioId={analysis.id} />

        {/* Top-Signale beider Welten */}
        <div>
          <p
            className="text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: "var(--text-muted)" }}
          >
            Top-Signale
          </p>

          <p
            className="text-[10px] font-semibold uppercase tracking-wider mb-1.5"
            style={{ color: "var(--text-muted)" }}
          >
            Bindung
          </p>
          {topBindung.length > 0 ? (
            topBindung.map((s) => (
              <div
                key={s.key}
                className="flex items-start gap-2 py-1.5"
                style={{ borderBottom: "1px solid var(--border)" }}
              >
                <ShieldAlert
                  size={12}
                  className="mt-0.5 shrink-0"
                  style={{
                    color:
                      s.weight >= 0.2
                        ? "var(--risk-high)"
                        : s.weight >= 0.1
                          ? "var(--accent)"
                          : "var(--accent-gold)",
                  }}
                />
                <span
                  className="flex-1 text-[11px] leading-tight"
                  style={{ color: "var(--text-primary)" }}
                >
                  {s.label}
                </span>
                <span
                  className="text-[10px] font-mono-data shrink-0"
                  style={{ color: "var(--text-muted)" }}
                >
                  {(s.weight * 100).toFixed(0)}%
                </span>
              </div>
            ))
          ) : (
            <div className="flex items-center gap-2 py-1.5">
              <CheckCircle2 size={12} style={{ color: "var(--risk-low)" }} />
              <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                Keine ausgelösten Signale
              </span>
            </div>
          )}

          <p
            className="text-[10px] font-semibold uppercase tracking-wider mt-3 mb-1.5"
            style={{ color: "var(--text-muted)" }}
          >
            Wirtschaft
          </p>
          {wirtschaftLoading && (
            <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
              lädt…
            </span>
          )}
          {wirtschaftError && (
            <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
              Wirtschaftsdaten nicht verfügbar
            </span>
          )}
          {wirtschaft &&
            (topAlerts.length > 0 ? (
              <div className="flex flex-col gap-2">
                {topAlerts.map((alert) => (
                  <WirtschaftAlertRow key={alert.message} alert={alert} />
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 py-1.5">
                <CheckCircle2 size={12} style={{ color: "var(--risk-low)" }} />
                <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                  Keine Auffälligkeiten
                </span>
              </div>
            ))}
        </div>
      </>
    );
  };

  // ── W26 Reiter „Wirtschaftlichkeit": KPI-Kacheln, Verlauf, Ampel-Alerts ────

  const renderWirtschaft = () => {
    if (wirtschaftLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-32 gap-2">
          <RefreshCw size={16} className="animate-spin" style={{ color: "var(--accent)" }} />
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            Wirtschaftsdaten werden geladen…
          </span>
        </div>
      );
    }
    if (wirtschaftError || !wirtschaft) {
      return (
        <div className="flex items-center gap-2 py-4">
          <AlertCircle size={14} style={{ color: "var(--risk-high)" }} />
          <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Wirtschaftsdaten konnten nicht geladen werden
          </span>
        </div>
      );
    }

    const k = wirtschaft.kpis;
    // Seit dem 09.09.2026 aus `lib/glossar.ts` — dieselbe Quelle wie die
    // Übersicht und „Mein Team“. Vorher lieferte das Backend die Texte nur
    // an dieses Fenster, die anderen Sichten hatten gar keine Erklärung.
    const glossar = GLOSSAR;
    const chartData = wirtschaft.verlauf.map((p) => ({ ...p, label: fmtYearMonth(p.yearMonth) }));
    const ohneKosten = wirtschaft.verlauf.every((p) => p.kosten == null);

    type Kachel = { key: string; label: string; value: string; valueColor?: string };

    // Zwei Bloecke, bewusst getrennt (Marie/Susi 31.08.2026): Kennzahlen, die
    // ZUSAMMEN eine Frage beantworten, muessen denselben Zeitraum haben. Vorher
    // stand Umsatz aus 12 Monaten neben einer Auslastung aus 3, ohne dass man
    // es sah — Noel Thorwesten las sich mit 99 % Auslastung gut und hatte
    // ueber das Jahr 57 % und einen negativen Deckungsbeitrag.
    // Entfernt am 03.09.2026 auf Maries Zuruf: Stundensatz-Marge 12M,
    // Festpreis-Arbeit 12M und „davon bewertet“. Die Werte werden weiter
    // BERECHNET — die Festpreis-Bewertung fließt in den Umsatz (Pauls Regel)
    // und die Festpreis-Stunden fallen aus dem Stundensatz-Nenner. Nur die
    // Kacheln sind weg.
    const kachelnJahr: Kachel[] = [
      {
        key: "deckungsbeitrag12m",
        // Betrag UND Marge in einer Kachel (Marie, 04.09.2026). Der Betrag sagt,
        // wie viel Geld übrig bleibt, die Marge wie gut das im Verhältnis zum
        // Umsatz ist — zusammen beantworten sie erst die Frage. Ohne Umsatz gibt
        // es keinen Nenner, dann steht nur der Betrag da.
        label: "Deckungsbeitrag / Marge 12M",
        value: ohneKosten
          ? "—"
          : k.deckungsbeitragPct12m == null
            ? fmtEur0.format(k.deckungsbeitrag12m)
            : `${fmtEur0.format(k.deckungsbeitrag12m)} · ${k.deckungsbeitragPct12m} %`,
        valueColor: !ohneKosten && k.deckungsbeitrag12m < 0 ? "var(--risk-high-text)" : undefined,
      },
      { key: "umsatz12m", label: "Umsatz 12M", value: fmtEur0.format(k.umsatz12m) },
      {
        key: "kosten12m",
        label: "Kosten 12M",
        // Ohne hinterlegte Rollenkosten waere der Deckungsbeitrag gleich dem
        // Umsatz — das saehe gut aus und waere falsch. Deshalb "—" statt 0.
        value: ohneKosten ? "—" : fmtEur0.format(k.kosten12m),
      },
      {
        key: "utilizationAvg12m",
        label: "Auslastung 12M",
        value: k.utilizationAvg12m != null ? `${k.utilizationAvg12m} %` : "—",
      },
      {
        key: "sickDays12m",
        label: "Krankheitsausfall 12M",
        value: arbeitstageText(k.sickDays12m),
      },
    ];

    const kachelnAktuell: Kachel[] = [
      {
        key: "utilizationAvg3m",
        // MTB wird an Pipeline und Win-Rate gemessen, nicht an der Faktura
        // (Timm 12.08.) — die Zahl steht da, wird aber nicht bewertet.
        label: k.utilizationRated === false ? "Auslastung Ø3M (ohne Ziel)" : "Auslastung Ø3M",
        value: k.utilizationAvg3m != null ? `${k.utilizationAvg3m} %` : "—",
      },
      {
        key: "hourlyRate3m",
        label: "Ø Stundensatz",
        value: k.hourlyRate3m != null ? `${fmtEur0.format(k.hourlyRate3m)}/h` : "—",
      },
      {
        key: "deckungsbeitragPct3m",
        // Bezug der Marge ist der UMSATZ, nicht die Kosten — Maries Definition
        // vom 03.09.2026. Seit 04.09. steht der Betrag daneben: Ohne ihn sagt
        // eine Marge nichts über die Größenordnung. Ohne Umsatz kein Nenner,
        // dann steht nur der Betrag da.
        label: "Deckungsbeitrag / Marge Ø3M",
        value: ohneKosten
          ? "—"
          : k.deckungsbeitragPct3m == null
            ? fmtEur0.format(k.deckungsbeitrag3m)
            : `${fmtEur0.format(k.deckungsbeitrag3m)} · ${k.deckungsbeitragPct3m} %`,
        valueColor: !ohneKosten && k.deckungsbeitrag3m < 0 ? "var(--risk-high-text)" : undefined,
      },
      {
        key: "monthlyCost",
        label: "Monatskosten",
        value: k.monthlyCost != null ? fmtEur0.format(k.monthlyCost) : "—",
      },
    ];

    return (
      <>
        {/* Demo-Banner (Etappe 0) */}
        {wirtschaft.dataBasis === "demo" && <DemoDatenbasisBanner />}

        {/* Zwei Bloecke mit eigenem Zeitraum in der Ueberschrift. Ohne diese
            Trennung standen 12-Monats- und 3-Monats-Zahlen nebeneinander und
            widersprachen sich scheinbar. */}
        <div>
          <p
            className="text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: "var(--text-muted)" }}
          >
            Das Jahr · letzte 12 abgeschlossene Monate
          </p>
          <div className="grid grid-cols-2 gap-2">
            {kachelnJahr.map((kachel) => (
              <WirtKpiTile
                key={kachel.key}
                label={kachel.label}
                value={kachel.value}
                valueColor={kachel.valueColor}
                glossarEntry={glossar[kachel.key]}
                hero={kachel.key === "deckungsbeitrag12m"}
              />
            ))}
          </div>
        </div>

        {/* Kohorten-Einordnung ("Im Vergleich · gleiche Karrierestufe") auf
            Maries Zuruf vom 04.09.2026 ausgeblendet: "nimm diese Ansicht erst
            wieder raus". Bewusst nur die ANZEIGE — `KohortenEinordnung` und die
            Berechnung im Backend (`baueKohorten`, `vergleicheMitKohorte`) bleiben
            stehen, damit der Block mit einer Zeile zurückkommt:
            {wirtschaft.kohorte && <KohortenEinordnung kohorte={wirtschaft.kohorte} />} */}

        <div>
          <p
            className="text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: "var(--text-muted)" }}
          >
            Aktuell · Ø 3 Monate — Grundlage der Ampel
          </p>
          <div className="grid grid-cols-2 gap-2">
            {kachelnAktuell.map((kachel) => (
              <WirtKpiTile
                key={kachel.key}
                label={kachel.label}
                value={kachel.value}
                valueColor={kachel.valueColor}
                glossarEntry={glossar[kachel.key]}
              />
            ))}
          </div>
        </div>

        {/* Umsatz/Kosten-Verlauf */}
        <div>
          <p
            className="text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: "var(--text-muted)" }}
          >
            Umsatz & Kosten je Monat
          </p>
          <div className="card p-3">
            <ResponsiveContainer width="100%" height={190}>
              <ComposedChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="var(--chart-grid)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  interval={2}
                  tick={{ fontSize: 9, fill: "var(--text-muted)" as string }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={34}
                  tickFormatter={fmtEurAxis}
                  tick={{ fontSize: 9, fill: "var(--text-muted)" as string }}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--bg-card)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    fontSize: 11,
                    color: "var(--text-primary)",
                  }}
                  formatter={(v: number, name: string) => [fmtEur0.format(v), name]}
                  labelFormatter={(l: string) => l}
                />
                <Legend wrapperStyle={{ fontSize: 10, color: "var(--text-muted)" }} iconSize={9} />
                {/* Serie 1 = --chart-1, Kostenlinie = --chart-reference gestrichelt */}
                <Bar
                  dataKey="umsatz"
                  name="Umsatz"
                  fill="var(--chart-1)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={28}
                />
                <Line
                  type="monotone"
                  dataKey="kosten"
                  name="Kosten"
                  stroke="var(--chart-reference)"
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={false}
                  activeDot={{ r: 3.5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
            {ohneKosten && (
              <p className="text-[10px] mt-1.5" style={{ color: "var(--text-muted)" }}>
                Keine Kostenlinie — für die Rolle ist kein Kostensatz hinterlegt.
              </p>
            )}
          </div>
        </div>

        {/* Ampel-Alert-Liste */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: "var(--text-muted)" }}
            >
              Ampel-Signale
            </p>
            <WirtschaftAmpelBadge level={wirtschaft.ampel.level} />
          </div>
          {wirtschaft.ampel.alerts.length > 0 ? (
            <div className="flex flex-col gap-2">
              {wirtschaft.ampel.alerts.map((alert) => (
                <WirtschaftAlertRow key={alert.message} alert={alert} />
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 py-1">
              <CheckCircle2 size={12} style={{ color: "var(--risk-low)" }} />
              <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                Keine Auffälligkeiten — alle Kennzahlen im Rahmen
              </span>
            </div>
          )}
        </div>

        <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>
          Stand: letzter abgeschlossener Monat {fmtYearMonth(wirtschaft.lastClosedMonth)}
        </p>
      </>
    );
  };

  // ── Reiter „Bindung": kompletter bisheriger Drawer-Inhalt (unverändert) ────

  const renderBindung = () => (
    <>
      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center h-32 gap-2">
          <RefreshCw size={16} className="animate-spin" style={{ color: "var(--accent)" }} />
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            Profil wird geladen…
          </span>
        </div>
      )}

      {isError && (
        <div className="flex items-center gap-2 py-4">
          <AlertCircle size={14} style={{ color: "var(--risk-high)" }} />
          <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Fehler beim Laden des Profils
          </span>
        </div>
      )}

      {analysis && (
        <>
          {/* ── Score Summary ── */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <span
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: "var(--text-muted)" }}
              >
                Risiko-Score
              </span>
              <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                Konfidenz {(analysis.confidence * 100).toFixed(0)}%
              </span>
            </div>
            <div className="flex items-end gap-3 mb-3">
              <span
                className="text-3xl font-bold font-mono-data"
                style={{ color: riskTextColor(analysis.riskLevel) }}
              >
                {(analysis.retentionScore * 100).toFixed(0)}%
              </span>
              <span className="text-xs pb-1" style={{ color: "var(--text-muted)" }}>
                Abgangswahrscheinlichkeit
              </span>
            </div>
            {/* Score bar */}
            <div
              className="h-2 rounded-full overflow-hidden"
              style={{ background: "var(--bg-surface)" }}
            >
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${analysis.retentionScore * 100}%`,
                  background: riskColor(analysis.riskLevel),
                }}
              />
            </div>
            {/* Triggered count */}
            <div
              className="flex justify-between mt-2 text-[10px]"
              style={{ color: "var(--text-muted)" }}
            >
              <span>
                {triggered.length} von {analysis.signals.length} Signalen ausgelöst
              </span>
              {analysis.team && <span style={{ color: "var(--accent)" }}>{analysis.team}</span>}
            </div>
          </div>

          {/* ── Gehaltsposition (Betrag + Vergleich zur gleichen Rolle/Stufe) ── */}
          {analysis.salaryPosition &&
            (() => {
              const sp = analysis.salaryPosition;
              const below = sp.deltaPct != null && sp.deltaPct < 0;
              const deltaColor =
                sp.deltaPct == null
                  ? "var(--text-muted)"
                  : sp.deltaPct <= -10
                    ? "var(--risk-high)"
                    : sp.deltaPct < 0
                      ? "var(--risk-medium)"
                      : "var(--risk-low)";
              return (
                <div
                  className="rounded-lg p-3"
                  style={{ background: "var(--bg-hover)", border: "1px solid var(--border)" }}
                >
                  <p
                    className="text-[10px] font-semibold uppercase tracking-wider mb-1.5"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Gehaltsposition
                  </p>
                  <div className="flex items-baseline justify-between">
                    <span className="text-lg font-bold" style={{ color: "var(--text-primary)" }}>
                      {sp.annualSalary.toLocaleString("de-DE")} €
                    </span>
                    <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                      / Jahr
                    </span>
                  </div>
                  {/* Teilzeit: Vergleich läuft auf Vollzeit-Basis — transparent machen */}
                  {sp.fteAnnualSalary !== sp.annualSalary && (
                    <div className="mt-0.5 text-[10px]" style={{ color: "var(--text-muted)" }}>
                      Teilzeit{sp.weeklyHours != null ? ` (${sp.weeklyHours} h/Woche)` : ""} —
                      Vollzeit-Äquivalent {sp.fteAnnualSalary.toLocaleString("de-DE")} €, der
                      Vergleich unten läuft auf Vollzeit-Basis
                    </div>
                  )}
                  {sp.median != null && sp.deltaPct != null ? (
                    <div className="mt-1 text-[11px]" style={{ color: deltaColor }}>
                      {sp.deltaPct === 0 ? (
                        <>
                          Genau im Median von <b>{sp.group}</b>
                        </>
                      ) : (
                        <>
                          <b>{Math.abs(sp.deltaPct)}%</b> {below ? "unter" : "über"} dem Median von{" "}
                          <b>{sp.group}</b>
                        </>
                      )}
                      <span style={{ color: "var(--text-muted)" }}>
                        {" "}
                        · Median {sp.median.toLocaleString("de-DE")} € · {sp.groupSize} Personen
                      </span>
                    </div>
                  ) : (
                    <div className="mt-1 text-[11px]" style={{ color: "var(--text-muted)" }}>
                      Vergleich nicht verfügbar (zu kleine Gruppe: {sp.groupSize}{" "}
                      {sp.groupSize === 1 ? "Person" : "Personen"})
                    </div>
                  )}
                </div>
              );
            })()}

          {/* ── Signal Breakdown ── */}
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-wider mb-3"
              style={{ color: "var(--text-muted)" }}
            >
              Signal-Breakdown
            </p>

            {triggered.length > 0 && (
              <div className="mb-3">
                <p
                  className="text-[10px] font-medium mb-1.5 flex items-center gap-1.5"
                  style={{ color: "var(--risk-high)" }}
                >
                  <ShieldAlert size={10} /> Ausgelöste Signale ({triggered.length})
                </p>
                {triggered
                  .slice()
                  .sort((a, b) => b.weight - a.weight)
                  .map((s) => (
                    <SignalRow key={s.key} signal={s} maxWeight={maxWeight} />
                  ))}
              </div>
            )}

            {positives.length > 0 && (
              <div className="mb-3">
                <p
                  className="text-[10px] font-medium mb-1.5 flex items-center gap-1.5"
                  style={{ color: "var(--risk-low)" }}
                >
                  <CheckCircle2 size={10} /> Stabilisierende Faktoren ({positives.length})
                </p>
                {positives.map((s) => (
                  <div
                    key={s.key}
                    className="flex items-start gap-2 py-1.5"
                    style={{ borderBottom: "1px solid var(--border)" }}
                  >
                    <CheckCircle2
                      size={12}
                      className="mt-0.5 shrink-0"
                      style={{ color: "var(--risk-low)" }}
                    />
                    <span
                      className="flex-1 text-[11px] leading-tight"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {untriggered.length > 0 && (
              <details>
                <summary
                  className="text-[10px] cursor-pointer select-none mb-1.5 flex items-center gap-1.5"
                  style={{ color: "var(--text-muted)", listStyle: "none" }}
                >
                  <CheckCircle2 size={10} style={{ color: "var(--risk-low)" }} />
                  Nicht ausgelöst ({untriggered.length})
                </summary>
                {untriggered.map((s) => (
                  <SignalRow key={s.key} signal={s} maxWeight={maxWeight} />
                ))}
              </details>
            )}
          </div>

          {/* ── Event-Teilnahme ── */}
          {/* W3: Karte nur zeigen, wenn es mind. ein gepflegtes Quartal gibt —
                  sonst suggeriert eine Karte voller "—" fälschlich fehlende Teilnahme. */}
          {analysis.events?.some((e) => e.explicit) && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p
                  className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"
                  style={{ color: "var(--text-muted)" }}
                >
                  <Calendar size={11} />
                  Event-Teilnahme
                </p>
                {analysis.eventAttendanceRate != null && (
                  <span
                    className="text-[10px] font-mono-data px-1.5 py-0.5 rounded"
                    style={{
                      color:
                        analysis.eventAttendanceRate >= 0.7
                          ? "var(--risk-low-text)"
                          : analysis.eventAttendanceRate >= 0.3
                            ? "var(--accent-gold)"
                            : "var(--risk-high-text)",
                      background:
                        analysis.eventAttendanceRate >= 0.7
                          ? "var(--risk-low-bg)"
                          : analysis.eventAttendanceRate >= 0.3
                            ? "var(--risk-med-bg)"
                            : "var(--risk-high-bg)",
                    }}
                  >
                    {Math.round(analysis.eventAttendanceRate * 100)}% Teilnahme
                  </span>
                )}
              </div>
              <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
                {analysis.events.map((ev) => {
                  // W3: nur explizit gepflegte Quartale als ✓/✗; leere (abgeleitete)
                  // Quartale neutral als "—", damit sie nicht wie Nicht-Teilnahme wirken.
                  if (!ev.explicit)
                    return (
                      <div
                        key={ev.quarter}
                        className="rounded p-1.5 text-center"
                        style={{
                          background: "var(--bg-card)",
                          border: "1px solid var(--border)",
                          opacity: 0.3,
                        }}
                      >
                        <div
                          className="text-[8px] font-mono-data"
                          style={{ color: "var(--text-muted)" }}
                        >
                          {ev.quarter.replace(" ", " ")}
                        </div>
                        <div className="text-[9px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                          {"—"}
                        </div>
                      </div>
                    );
                  return (
                    <div
                      key={ev.quarter}
                      className="rounded p-1.5 text-center"
                      title={ev.label}
                      style={{
                        background: ev.attended ? "var(--risk-low-bg)" : "var(--risk-high-bg)",
                        border: `1px solid ${ev.attended ? "var(--risk-low-bg)" : "var(--risk-high-bg)"}`,
                      }}
                    >
                      <div
                        className="text-[8px] font-mono-data"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {ev.quarter.replace(" ", " ")}
                      </div>
                      <div
                        className="text-[9px] font-semibold mt-0.5"
                        style={{
                          color: ev.attended ? "var(--risk-low-text)" : "var(--risk-high-text)",
                        }}
                      >
                        {ev.attended ? "✓" : "✗"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Eigene Krankheitstage (Arbeitstage, ohne „Kind krank") ── */}
          {analysis.sickDayStats && (
            <EigeneKrankheitstage werte={analysis.sickDayStats} seitEintritt={inProbezeit} />
          )}

          {/* ── Maßnahmen-Tracking ── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p
                className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"
                style={{ color: "var(--text-muted)" }}
              >
                <ClipboardList size={11} />
                Maßnahmen
                {massnahmenDaten?.stats &&
                  massnahmenDaten.stats.open + massnahmenDaten.stats.inProgress > 0 && (
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded font-mono-data"
                      style={{ background: "var(--accent-dim)", color: "var(--accent)" }}
                    >
                      {massnahmenDaten.stats.open + massnahmenDaten.stats.inProgress} aktiv
                    </span>
                  )}
              </p>
              <button
                type="button"
                className="flex items-center gap-1 text-[10px] px-2 py-1 rounded"
                style={{ border: "1px solid var(--border)", color: "var(--text-muted)" }}
                onClick={() => {
                  // Ein Fehler gehört zum gescheiterten Versuch, nicht zum nächsten Formular.
                  createMutation.reset();
                  setShowNewForm(!showNewForm);
                }}
              >
                <Plus size={10} />
                Neue Maßnahme
              </button>
            </div>

            {/* New Intervention Form */}
            {showNewForm && (
              <div
                className="rounded-lg p-3 mb-3 flex flex-col gap-2"
                style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border-light)",
                }}
              >
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as InterventionType)}
                  className="text-xs px-2 py-1.5 rounded"
                  style={{
                    background: "var(--bg-hover)",
                    border: "1px solid var(--border)",
                    color: "var(--text-primary)",
                    outline: "none",
                  }}
                >
                  {(Object.keys(INTERVENTION_LABELS) as InterventionType[]).map((t) => (
                    <option key={t} value={t}>
                      {INTERVENTION_LABELS[t]}
                    </option>
                  ))}
                </select>
                <textarea
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder="Notizen (optional)…"
                  rows={2}
                  className="text-xs px-2 py-1.5 rounded resize-none"
                  style={{
                    background: "var(--bg-hover)",
                    border: "1px solid var(--border)",
                    color: "var(--text-primary)",
                    outline: "none",
                  }}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="flex-1 text-xs py-1.5 rounded font-medium"
                    style={{ background: "var(--accent)", color: "#fff" }}
                    onClick={() => createMutation.mutate({ type: newType, notes: newNotes })}
                    disabled={createMutation.isPending}
                  >
                    {createMutation.isPending ? "Speichert…" : "Speichern"}
                  </button>
                  <button
                    type="button"
                    className="text-xs px-3 py-1.5 rounded"
                    style={{ border: "1px solid var(--border)", color: "var(--text-muted)" }}
                    onClick={() => {
                      createMutation.reset();
                      setShowNewForm(false);
                    }}
                  >
                    Abbrechen
                  </button>
                </div>
                {createMutation.isError && (
                  <div className="flex items-start gap-2 py-1">
                    <AlertCircle
                      size={13}
                      style={{ color: "var(--risk-high)", flexShrink: 0, marginTop: 1 }}
                    />
                    <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                      {massnahmenFehlerText("anlegen", createMutation.error)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Bestehende Maßnahmen. Was hier steht, entscheidet `massnahmenListenZustand`: Der
                Satz „keine Maßnahmen“ erscheint nur nach einer erfolgreichen Antwort ohne
                Maßnahmen, nie während des Ladens und nie nach einem Fehler. */}
            {massnahmenZustand === "liste" && (
              <div className="flex flex-col gap-2">
                {massnahmenDaten?.interventions.map((i) => (
                  <InterventionItem
                    key={i.id}
                    intervention={i}
                    onStatusChange={(id, status) => updateMutation.mutate({ id, status })}
                  />
                ))}
              </div>
            )}
            {(massnahmenZustand === "laedt" || massnahmenZustand === "leer") && (
              <p className="text-xs py-2" style={{ color: "var(--text-muted)" }}>
                {massnahmenHinweisText(massnahmenZustand)}
              </p>
            )}
            {massnahmenZustand === "fehler" && (
              <div className="flex items-start gap-2 py-3">
                <AlertCircle
                  size={13}
                  style={{ color: "var(--risk-high)", flexShrink: 0, marginTop: 1 }}
                />
                <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                  {massnahmenFehlerText("lesen", interventionError)}
                </span>
              </div>
            )}

            {updateMutation.isError && (
              <div className="flex items-center gap-2 py-3">
                <AlertCircle size={13} style={{ color: "var(--risk-high)" }} />
                <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                  Der Status der Maßnahme konnte nicht geändert werden.
                </span>
              </div>
            )}

            {massnahmenDaten?.stats && massnahmenDaten.stats.done > 0 && (
              <div
                className="flex items-center gap-1 mt-2 text-[10px]"
                style={{ color: "var(--risk-low)" }}
              >
                <CheckCheck size={10} />
                {massnahmenDaten.stats.done} abgeschlossen
              </div>
            )}
          </div>

          {/* ── FK-Empfehlungen ── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: "var(--text-muted)" }}
              >
                FK-Maßnahmen
              </p>
              {!tipsFetched && !tipsLoading && (
                <button
                  type="button"
                  onClick={() => void fetchTips()}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors"
                  style={{
                    background: "var(--accent-dim)",
                    color: "var(--accent)",
                    border: "1px solid var(--accent-dim)",
                  }}
                >
                  <Sparkles size={11} />
                  Empfehlungen generieren
                </button>
              )}
            </div>

            {tipsLoading && (
              <div
                className="flex items-center justify-center gap-2 py-6 rounded-lg"
                style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
              >
                <RefreshCw size={13} className="animate-spin" style={{ color: "var(--accent)" }} />
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                  KI analysiert Signale mit mindsquare-Werten…
                </span>
              </div>
            )}

            {tipsError && (
              <div className="flex items-center gap-2 py-3">
                <AlertCircle size={13} style={{ color: "var(--risk-high)" }} />
                <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                  Empfehlungen konnten nicht generiert werden.
                </span>
              </div>
            )}

            {tips && (
              <div className="flex flex-col gap-3">
                {/* Escalation warning */}
                {tips.escalateToHrbp && (
                  <div
                    className="flex items-start gap-2 p-3 rounded-lg"
                    style={{
                      background: "var(--risk-high-bg)",
                      border: "1px solid var(--risk-high-bg)",
                    }}
                  >
                    <AlertTriangle
                      size={13}
                      style={{ color: "var(--risk-high-text)", flexShrink: 0, marginTop: 1 }}
                    />
                    <div>
                      <p
                        className="text-[11px] font-semibold mb-0.5"
                        style={{ color: "var(--risk-high-text)" }}
                      >
                        HRBP einbeziehen
                      </p>
                      <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                        {tips.escalateReason ?? "Situation erfordert HRBP-Unterstützung."}
                      </p>
                    </div>
                  </div>
                )}

                {/* Summary */}
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}
                >
                  {tips.riskSummary}
                </p>

                {/* Tips by timeframe */}
                {tipsByTimeframe.map(({ tf, items }) => (
                  <div key={tf}>
                    <div className="flex items-center gap-2 mb-1.5">
                      {(() => {
                        const meta = TIMEFRAME_META[tf];
                        const Icon = meta.Icon;
                        return <Icon size={10} style={{ color: meta.color }} />;
                      })()}
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider"
                        style={{ color: TIMEFRAME_META[tf].color }}
                      >
                        {TIMEFRAME_META[tf].label}
                      </span>
                    </div>
                    <div className="flex flex-col gap-2">
                      {items.map((tip, i) => (
                        <TipCard key={i} tip={tip} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
      }}
      onClick={handleBackdropClick}
    >
      <div
        ref={drawerRef}
        className="h-full flex flex-col overflow-hidden"
        style={{
          width: 480,
          background: "var(--bg-surface)",
          borderLeft: "1px solid var(--border)",
          borderTopLeftRadius: "var(--radius-lg)",
          borderBottomLeftRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-lift)",
          animation: "slideInRight 240ms var(--ease)",
        }}
      >
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); opacity: 0; }
            to   { transform: translateX(0);    opacity: 1; }
          }
        `}</style>

        {/* ── Header ── */}
        <div
          className="flex items-center gap-3 px-5 py-4 shrink-0"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          {analysis ? (
            <>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className="text-sm font-semibold truncate"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {analysis.displayName}
                  </span>
                  {analysis.isConfirmedLeaving && (
                    <span
                      className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold"
                      style={{
                        background: "var(--risk-high-bg)",
                        color: "var(--risk-high-text)",
                        border: "1px solid var(--risk-high-bg)",
                      }}
                    >
                      <UserX size={9} /> Abgang bestätigt
                    </span>
                  )}
                </div>
                <div
                  className="flex items-center gap-2 text-[11px]"
                  style={{ color: "var(--text-muted)" }}
                >
                  <span>{analysis.department ?? "—"}</span>
                  {analysis.position && (
                    <>
                      <ChevronRight size={10} />
                      <span>{analysis.position}</span>
                    </>
                  )}
                  {analysis.tenureMonths != null && (
                    <>
                      <span style={{ opacity: 0.5 }}>·</span>
                      <span>{analysis.tenureMonths}M Betriebszugehörigkeit</span>
                    </>
                  )}
                </div>
              </div>
              {analysis.riskLevel && <RiskBadge level={analysis.riskLevel} />}
            </>
          ) : (
            <div className="flex-1">
              <div
                className="h-4 w-32 rounded animate-pulse"
                style={{ background: "var(--bg-hover)" }}
              />
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 transition-colors ml-2"
            style={{ color: "var(--text-muted)", background: "transparent", border: "none" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--bg-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* ── Reiter-Leiste (W26 Ebene 2) ── */}
        <div
          className="flex items-center gap-3 px-5 py-2.5 shrink-0"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <div className="segmented">
            {DRAWER_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                data-active={tab === t.key}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* Kennzeichnung reiterunabhängig: der Marker bleibt auch stehen,
              wenn gerade „Bindung" offen ist (dort echte Personio-Daten). */}
          {wirtschaft?.dataBasis === "demo" && <DemoTabMarker />}
        </div>

        {/* ── Scroll body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
          {tab === "ueberblick" && renderUeberblick()}
          {tab === "bindung" && renderBindung()}
          {tab === "wirtschaft" && renderWirtschaft()}
        </div>

        {/* ── Footer ── */}
        {analysis && (
          <div
            className="px-5 py-3 shrink-0 flex items-center justify-end text-[10px]"
            style={{ borderTop: "1px solid var(--border)", color: "var(--text-muted)" }}
          >
            <span>
              Score berechnet{" "}
              {new Date(analysis.calculatedAt).toLocaleTimeString("de-DE", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
